// Which workspace subdomain the app is being served from.
//
// This was computed inline in src/routes/index.tsx and nowhere else, so any
// second caller had to copy the reserved-name list — and a copy that drifts is
// how "we're on the X subdomain" quietly starts disagreeing with the router.
// One function, one list.

/**
 * Hosts that are OURS, not a company workspace, per environment. A request to
 * one of these has no workspace subdomain at all.
 *
 * `link` is the WhatsApp short-link redirector (link.ourteam.ai, #2935) — it is
 * reserved everywhere; without that the SubdomainProvider fetches a company
 * called "link", 404s, and takes the whole app over with NotFoundPage.
 */
const RESERVED_BY_ENV: Record<string, string[]> = {
    production: ["app", "www", "link"],
    staging: ["staging", "www", "link"],
};
const RESERVED_DEFAULT = ["localhost", "app", "www", "link"];

/**
 * The workspace subdomain of the current host, or `null` on a reserved host.
 *
 * Read from `window.location.hostname`, so it is synchronous and correct on the
 * very first render — unlike the `CURRENT_COMPANY` cookie, which is only written
 * after SubdomainProvider's company lookup resolves.
 */
export function getCurrentSubdomain(): string | null {
    const hostname = window.location.hostname;
    const subdomain = hostname.split(".")[0];
    const reserved = RESERVED_BY_ENV[import.meta.env.VITE_APP_ENV] ?? RESERVED_DEFAULT;
    return reserved.includes(subdomain) ? null : subdomain;
}

/**
 * SPA is an assessment partner whose candidates sign up on their own subdomain.
 * Nobody registers as an employer there, so the login page offers the candidate
 * path only.
 */
export const SPA_SUBDOMAIN = "spa";

/** True on spa.ourteam.ai (and spa.localhost in dev). */
export function isSpaSubdomain(): boolean {
    return getCurrentSubdomain() === SPA_SUBDOMAIN;
}
